/**
 * Web Push Notification Service
 * Uses Firebase Cloud Messaging to receive push notifications on web.
 * Same FCM infrastructure as the mobile app.
 */

import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { app } from '../config/firebase'

const VAPID_KEY = 'BP7CDPlFj6pLh5OIt2T90jpHRa8pxrAN4x1PoGn5x9vGqj95Z0DNF4mrIiJ13Jd2ICuJUJ8J891PVjlb0y0QhYM'
const STORAGE_KEY = 'web_notifications'

export interface WebNotification {
  id: string
  type: 'like' | 'comment' | 'follow' | 'repost' | 'mention' | 'new_post' | 'reply' | 'general'
  userId: string
  userName: string
  userAvatar?: string
  content?: string
  postId?: string
  timestamp: string
  read: boolean
}

type NotificationCallback = (notification: WebNotification) => void

class WebPushService {
  private messaging: any = null
  private initialized = false
  private callbacks: NotificationCallback[] = []

  async initialize(): Promise<void> {
    if (this.initialized) return

    try {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        console.log('⚠️ Browser does not support push notifications')
        return
      }

      await navigator.serviceWorker.register('/firebase-messaging-sw.js')
      console.log('✅ FCM service worker registered')

      this.messaging = getMessaging(app)
      this.initialized = true

      // Listen for foreground messages
      onMessage(this.messaging, (payload) => {
        console.log('📱 Foreground push received:', payload)
        const notif = this.payloadToNotification(payload)
        this.saveNotification(notif)
        this.callbacks.forEach(cb => { try { cb(notif) } catch {} })

        // Show OS notification for foreground messages too
        if (Notification.permission === 'granted') {
          new Notification(payload.notification?.title || 'on record', {
            body: payload.notification?.body || '',
            icon: '/logo.svg',
            data: payload.data,
          })
        }
      })

      console.log('✅ Web Push Service initialized')
    } catch (error) {
      console.warn('⚠️ Web Push init failed (non-blocking):', error)
    }
  }

  async requestPermissionAndGetToken(): Promise<string | null> {
    try {
      if (!this.messaging) await this.initialize()
      if (!this.messaging) return null

      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        console.log('❌ Notification permission denied')
        return null
      }

      const token = await getToken(this.messaging, { vapidKey: VAPID_KEY })
      if (token) {
        console.log('🔑 FCM web token obtained')
        await this.sendTokenToBackend(token)
        return token
      }

      return null
    } catch (error) {
      console.warn('⚠️ FCM token error:', error)
      return null
    }
  }

  private async sendTokenToBackend(token: string): Promise<void> {
    try {
      const authToken = localStorage.getItem('auth_token') || localStorage.getItem('token')
      if (!authToken) return

      await fetch('https://auth.onrecord.live/auth/update-fcm-token', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fcmToken: token }),
      })
      console.log('✅ FCM web token sent to backend')
    } catch (error) {
      console.warn('⚠️ Failed to send FCM token:', error)
    }
  }

  private payloadToNotification(payload: any): WebNotification {
    return {
      id: `web_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type: payload.data?.type || 'general',
      userId: payload.data?.senderId || '',
      userName: payload.notification?.title || 'on record',
      content: payload.notification?.body || '',
      postId: payload.data?.targetId || '',
      timestamp: new Date().toISOString(),
      read: false,
    }
  }

  private saveNotification(notif: WebNotification): void {
    try {
      const items = this.getNotifications()
      items.unshift(notif)
      if (items.length > 50) items.splice(50)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch {}
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  getNotifications(): WebNotification[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return []
      return JSON.parse(raw) as WebNotification[]
    } catch {
      return []
    }
  }

  getUnreadCount(): number {
    return this.getNotifications().filter(n => !n.read).length
  }

  markAsRead(id: string): void {
    const items = this.getNotifications()
    const item = items.find(n => n.id === id)
    if (item) {
      item.read = true
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    }
  }

  markAllAsRead(): void {
    const items = this.getNotifications().map(n => ({ ...n, read: true }))
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  }

  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY)
  }

  /** Subscribe to new incoming notifications. Returns unsubscribe function. */
  onNotification(cb: NotificationCallback): () => void {
    this.callbacks.push(cb)
    return () => {
      const i = this.callbacks.indexOf(cb)
      if (i > -1) this.callbacks.splice(i, 1)
    }
  }

  hasPermission(): boolean {
    return typeof Notification !== 'undefined' && Notification.permission === 'granted'
  }

  permissionDenied(): boolean {
    return typeof Notification !== 'undefined' && Notification.permission === 'denied'
  }
}

const webPushService = new WebPushService()
export default webPushService
